import { useEffect, useState, type FormEvent } from "react";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { MediaPickerModal } from "@/components/media/MediaPickerModal";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { Button } from "@/components/ui/Button";
import { useCurrentUser } from "@/features/auth/hooks";
import { roleAtLeast } from "@/features/auth/roles";
import { toSiteSettingsForm } from "@/features/site-settings/api";
import { useSiteSettings, useUpdateSiteSettings } from "@/features/site-settings/hooks";
import type { SiteSettingsForm } from "@/features/site-settings/types";

const emptyForm: SiteSettingsForm = {
  siteName: "",
  siteTagline: "",
  logoUrl: "",
  faviconUrl: "",
  homepageHeadline: "",
  homepageSubheadline: "",
  contactEmail: "",
  discordUrl: "",
  maintenanceMode: false,
  maintenanceMessage: "",
  maintenanceEstimatedReturn: "",
  maintenanceAllowOwnerPreview: true,
  maintenanceAllowDeveloperPreview: true,
  seoTitle: "",
  seoDescription: "",
  seoKeywords: "",
  ogTitle: "",
  ogDescription: "",
  ogImage: "",
  canonicalUrl: "",
  robotsIndex: true,
  robotsFollow: true,
};

export function SiteSettingsPage() {
  const currentUser = useCurrentUser();
  const settings = useSiteSettings();
  const updateSettings = useUpdateSiteSettings();
  const [form, setForm] = useState<SiteSettingsForm>(emptyForm);
  const [pickerTarget, setPickerTarget] = useState<"logoUrl" | "faviconUrl" | null>(null);
  const [message, setMessage] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const canEditMaintenance = roleAtLeast(currentUser.data?.role, "OWNER");

  useEffect(() => {
    if (settings.data) setForm(toSiteSettingsForm(settings.data));
  }, [settings.data]);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage(null);
    updateSettings.mutate(
      { input: form, includeMaintenanceMode: canEditMaintenance },
      {
        onSuccess: (data) => {
          setForm(toSiteSettingsForm(data));
          setMessage({ tone: "success", text: "Site settings saved." });
        },
        onError: (err) => setMessage({ tone: "error", text: err instanceof Error ? err.message : "Unable to save site settings." }),
      },
    );
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Site Settings"
        description="Manage the singleton site identity, contact, homepage metadata, and SEO settings."
        actions={<StatusBadge tone={form.maintenanceMode ? "warning" : "active"}>{form.maintenanceMode ? "Maintenance" : "Live"}</StatusBadge>}
      />
      {settings.isLoading ? (
        <div className="rounded-md border border-cyan-300/15 bg-black/25 p-6 text-sm text-content-secondary">Loading site settings...</div>
      ) : (
        <form className="space-y-6" onSubmit={handleSubmit}>
          <section className="grid gap-5 rounded-md border border-cyan-300/15 bg-black/25 p-4">
            <h3 className="font-display text-lg font-semibold uppercase text-white">Identity</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <TextField label="Site Name" value={form.siteName} onChange={(value) => setForm((current) => ({ ...current, siteName: value }))} required />
              <TextField label="Site Tagline" value={form.siteTagline} onChange={(value) => setForm((current) => ({ ...current, siteTagline: value }))} />
              <MediaUrlField label="Logo URL" value={form.logoUrl} onChange={(value) => setForm((current) => ({ ...current, logoUrl: value }))} onChoose={() => setPickerTarget("logoUrl")} />
              <MediaUrlField label="Favicon URL" value={form.faviconUrl} onChange={(value) => setForm((current) => ({ ...current, faviconUrl: value }))} onChoose={() => setPickerTarget("faviconUrl")} />
            </div>
          </section>
          <section className="grid gap-5 rounded-md border border-cyan-300/15 bg-black/25 p-4">
            <h3 className="font-display text-lg font-semibold uppercase text-white">Homepage Metadata</h3>
            <TextField
              label="Homepage Headline"
              value={form.homepageHeadline}
              onChange={(value) => setForm((current) => ({ ...current, homepageHeadline: value }))}
            />
            <TextArea
              label="Homepage Subheadline"
              value={form.homepageSubheadline}
              onChange={(value) => setForm((current) => ({ ...current, homepageSubheadline: value }))}
            />
          </section>
          <section className="grid gap-5 rounded-md border border-cyan-300/15 bg-black/25 p-4">
            <h3 className="font-display text-lg font-semibold uppercase text-white">Contact & SEO</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <TextField label="Contact Email" value={form.contactEmail} type="email" onChange={(value) => setForm((current) => ({ ...current, contactEmail: value }))} />
              <TextField label="Discord URL" value={form.discordUrl} type="url" onChange={(value) => setForm((current) => ({ ...current, discordUrl: value }))} />
              <TextField label="SEO Title" value={form.seoTitle} onChange={(value) => setForm((current) => ({ ...current, seoTitle: value }))} />
              <TextField label="SEO Keywords" value={form.seoKeywords} onChange={(value) => setForm((current) => ({ ...current, seoKeywords: value }))} />
              <TextField label="Canonical URL" value={form.canonicalUrl} type="url" onChange={(value) => setForm((current) => ({ ...current, canonicalUrl: value }))} />
              <TextField label="OG Image URL" value={form.ogImage} type="url" onChange={(value) => setForm((current) => ({ ...current, ogImage: value }))} />
            </div>
            <TextArea label="SEO Description" value={form.seoDescription} onChange={(value) => setForm((current) => ({ ...current, seoDescription: value }))} />
            <TextField label="OG Title" value={form.ogTitle} onChange={(value) => setForm((current) => ({ ...current, ogTitle: value }))} />
            <TextArea label="OG Description" value={form.ogDescription} onChange={(value) => setForm((current) => ({ ...current, ogDescription: value }))} />
            <div className="grid gap-3 sm:grid-cols-2">
              <ToggleField label="Robots Index" checked={form.robotsIndex} onChange={(value) => setForm((current) => ({ ...current, robotsIndex: value }))} />
              <ToggleField label="Robots Follow" checked={form.robotsFollow} onChange={(value) => setForm((current) => ({ ...current, robotsFollow: value }))} />
            </div>
          </section>
          <section className="grid gap-5 rounded-md border border-cyan-300/15 bg-black/25 p-4">
            <h3 className="font-display text-lg font-semibold uppercase text-white">Maintenance Mode</h3>
            <label className="flex items-center gap-3 rounded-md border border-cyan-300/15 bg-black/20 p-3">
              <input
                className="size-4 accent-cyan-300"
                type="checkbox"
                checked={form.maintenanceMode}
                disabled={!canEditMaintenance}
                onChange={(event) => setForm((current) => ({ ...current, maintenanceMode: event.target.checked }))}
              />
              <span className="text-sm text-content-secondary">Maintenance Mode</span>
            </label>
            <TextArea label="Maintenance Message" value={form.maintenanceMessage} onChange={(value) => setForm((current) => ({ ...current, maintenanceMessage: value }))} />
            <TextField label="Estimated Return" value={form.maintenanceEstimatedReturn} onChange={(value) => setForm((current) => ({ ...current, maintenanceEstimatedReturn: value }))} />
            <div className="grid gap-3 sm:grid-cols-2">
              <ToggleField label="Allow Owner Preview" checked={form.maintenanceAllowOwnerPreview} onChange={(value) => setForm((current) => ({ ...current, maintenanceAllowOwnerPreview: value }))} />
              <ToggleField label="Allow Developer Preview" checked={form.maintenanceAllowDeveloperPreview} onChange={(value) => setForm((current) => ({ ...current, maintenanceAllowDeveloperPreview: value }))} />
            </div>
            {!canEditMaintenance && <p className="text-xs text-content-muted">Only Owner and Developer roles can change maintenance mode.</p>}
          </section>
          {message && (
            <p
              className={
                message.tone === "success"
                  ? "rounded-md border border-teal-300/30 bg-teal-300/10 px-3 py-2 text-sm text-teal-100"
                  : "rounded-md border border-red-300/30 bg-red-500/10 px-3 py-2 text-sm text-red-100"
              }
            >
              {message.text}
            </p>
          )}
          <div className="flex justify-end">
            <Button type="submit" disabled={updateSettings.isPending}>
              {updateSettings.isPending ? "Saving..." : "Save Site Settings"}
            </Button>
          </div>
        </form>
      )}
      <MediaPickerModal
        isOpen={pickerTarget !== null}
        onClose={() => setPickerTarget(null)}
        onSelect={(items) => {
          const item = items[0];
          if (!item || !pickerTarget) return;
          setForm((current) => ({ ...current, [pickerTarget]: item.url }));
        }}
      />
    </div>
  );
}

function MediaUrlField({ label, value, onChange, onChoose }: { label: string; value: string; onChange: (value: string) => void; onChoose: () => void }) {
  return (
    <div>
      <TextField label={label} value={value} type="url" onChange={onChange} />
      <Button className="mt-2" type="button" size="sm" variant="secondary" onClick={onChoose}>Choose from Media</Button>
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
  type = "text",
  required,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: "text" | "url" | "email";
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="text-sm text-content-secondary">{label}</span>
      <input
        className="mt-2 h-11 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 text-sm text-white outline-none transition focus:border-cyan-300"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        type={type}
        required={required}
      />
    </label>
  );
}

function TextArea({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="block">
      <span className="text-sm text-content-secondary">{label}</span>
      <textarea
        className="mt-2 min-h-24 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 py-2 text-sm text-white outline-none transition focus:border-cyan-300"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function ToggleField({ label, checked, onChange }: { label: string; checked: boolean; onChange: (value: boolean) => void }) {
  return (
    <label className="flex items-center gap-3 rounded-md border border-cyan-300/15 bg-black/20 p-3">
      <input className="size-4 accent-cyan-300" type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span className="text-sm text-content-secondary">{label}</span>
    </label>
  );
}
